<template>
    <page-loading-indicator
        v-if="!accountBalanceReport"
        :number-of-breadcrumb-elements="2"
        actions
        :number-of-next-actions="0"
    />
    <page-wrapper v-else>
        <page-header :progress-bar="accountBalanceReport.currentQueueItem.isQueuedOrProcessing" progress-bar-indeterminate>
            <template v-slot:breadcrumbs>
                <q-breadcrumbs-el :label="$tc('common.accounting.account-balance-report.account-balance-report', 2)" :to="{ name: 'accounting-account-balance-reports' }" />
                <q-breadcrumbs-el :label="`${accountBalanceReport.formattedPeriod} – ${accountBalanceReport.accounts.length ? accountBalanceReport.accounts.length + ' ' + $tc('common.accounting.account.account', accountBalanceReport.accounts.length) : $tc('common.accounting.account.account-range', 1) + ' ' + accountBalanceReport.formattedAccountList.accounts}`" :to="{ name: 'accounting-account-balance-report-detail', params: { id: accountBalanceReport.id } }" >
                    <status-badge
                        :status="accountBalanceReport.currentQueueItem.status"
                        default-translation-base-path="common.status.queue-item-status"
                        :loading="accountBalanceReport.currentQueueItem.isQueuedOrProcessing"
                    />
                </q-breadcrumbs-el>
            </template>

            <h1>{{ $tc('common.accounting.account-balance-report.account-balance-report', 1) }}</h1>

            <template v-slot:actions>
                <actions
                    v-if="accountBalanceReport.actions"
                    :actions="accountBalanceReport.actions"
                    :number-of-next-best-actions="0"
                    translation-base-key="common.actions.accounting"
                    @click="handleActions"
                />

                <!-- Cancel Creation Account Balance Report -->
                <form-dialog
                    ref="cancelCreationDialog"
                    dont-close-on-confirm
                    @confirm="handleCancelCreation"
                >
                    <template v-slot:title>{{ $tc('common.actions.accounting.account-balance-report.cancel-creation', 1) }}</template>
                    <template v-slot:default>
                        <p>{{ $tc('views.accounting.account-balance-report.cancel-report-creation--confirm--n-of-x', 1) }}</p>
                    </template>
                </form-dialog>

                <!-- Delete Account Balance Report -->
                <form-dialog
                    ref="deleteDialog"
                    double-confirm
                    dont-close-on-confirm
                    @confirm="handleDelete"
                >
                    <template v-slot:title>{{ $tc('common.accounting.account-balance-report.delete-report', 1) }}</template>
                    <template v-slot:default>
                        <p>{{ $tc('views.accounting.account-balance-report.delete-report--confirm--n-of-x', 1) }}</p>
                    </template>
                </form-dialog>
            </template>
        </page-header>

        <div class="row q-col-gutter-md">
            <div v-if="hasStatusMessage(accountBalanceReport)" class="col-xs-12">
                <info-box :type="accountBalanceReport.currentQueueItem.status === QueueItemStatus.FAILED ? 'negative' : 'info'" no-margin>
                    <p><b>{{ $tc(`common.status.${kebabCase(accountBalanceReport.currentQueueItem.status)}`, 1) }}</b>: {{ accountBalanceReport.currentQueueItem.status_message.message }}</p>
                </info-box>
            </div>

            <grid-card
                :columns="{ xs: 12, md: 6 }"
                :heading="$tc('common.term.configuration', 1)"
                content-class="q-pb-sm"
            >
                <dl>
                    <div>
                        <dt>{{ $tc('common.accounting.account.account', accountBalanceReport.accounts.length || 2) }}: </dt>
                        <dd>{{ accountBalanceReportAccounts }} <info-icon v-if="accountBalanceReport.formattedAccountList.teaser" :text="accountBalanceReport.formattedAccountList.accounts" /></dd>
                    </div>
                    <div>
                        <dt>{{ $tc('common.term.period', 1) }}: </dt>
                        <dd>{{ accountBalanceReport.formattedPeriod }}</dd>
                    </div>
                    <div>
                        <dt>{{ $tc('common.file.file-type', 1) }}: </dt>
                        <dd><template v-for="(name, index) in accountBalanceReport.file_types">
                            <span :key="index">{{ $tc(`common.file.type.${kebabCase(name)}`, 1) }}<template v-if="index + 1 < accountBalanceReport.file_types.length">, </template></span>
                        </template></dd>
                    </div>
                </dl>
            </grid-card>

            <!-- TODO improvement @MTR: Refine when Drive has been done -->
            <grid-card
                v-if="accountBalanceReport.fileStorageItem"
                :columns="{ xs: 12, md: 6 }"
                :heading="$tc('common.file.file', 2)"
            >
                <template v-for="(entry, index) in accountBalanceReport.fileStorageItem.files">
                    <div :key="index">
                        {{ entry.name }} / {{ entry.formattedFileSize }}
                        <q-btn
                            color="primary"
                            size="sm"
                            flat
                            round
                            icon="mib-cloud-download"
                            @click="entry.download()"
                        />
                    </div>
                </template>
            </grid-card>

            <div class="col-xs-12">
                <p>
                    <small>
                        <base-button
                            show-as-link
                            @click="$refs.activityLogOverlay.open({ filterSubjects: [{ id: id, type: ActivityLogSubjectType.ACCOUNT_BALANCE_REPORT }] })"
                        >{{ $tc('common.term.created-by--on--date-time', 1, { name: accountBalanceReport.owner.getContactName(), date: accountBalanceReport.formattedCreatedAtDate, time: accountBalanceReport.formattedCreatedAtTime }) }}</base-button>
                    </small>
                </p>
            </div>
        </div>

        <!-- Polling -->
        <polling
            v-if="accountBalanceReport.currentQueueItem.isQueuedOrProcessing"
            ref="polling"
            :poll-fn="AccountBalanceReport.objects.get"
            :poll-filters="id"
            :stop-condition-fn="stopPollingConditionFn"
            auto-start
        />

        <!-- Activity log -->
        <activity-log-overlay ref="activityLogOverlay" />

        <in-page-footer>
            <base-button
                :label="$t('common.term.back')"
                flat
                primary-button
                @click="$router.go(-1)"
            />
        </in-page-footer>
    </page-wrapper>
</template>

<script>
import { kebabCase } from 'lodash'
import { AccountBalanceReport } from '@/models/models'
import { QueueItemStatus, ActivityLogSubjectType } from '@/enums/graphql'
import Actions from '@/components/Actions'

import StatusBadge from '@/components/StatusBadge'
import FormDialog from '@/components/form/FormDialog'
import Polling from '@/components/Polling.vue'
import ActivityLogOverlay from '@/components/ActivityLogOverlay'

export default {
    name: 'AccountBalanceReportDetail',
    components: {
        Actions,
        FormDialog,
        Polling,
        StatusBadge,
        ActivityLogOverlay,
    },
    meta () {
        return {
            title: !this.accountBalanceReport ? `${this.$t('common.term.loading')} …` : `${this.$tc('common.accounting.account-balance-report.account-balance-report', 1)}: ${this.accountBalanceReport.formattedPeriod} – ${this.accountBalanceReport.accounts.length ? this.accountBalanceReport.accounts.length + ' ' + this.$tc('common.accounting.account.account', this.accountBalanceReport.accounts.length) : this.$tc('common.accounting.account.account-range', 1) + ' ' + this.accountBalanceReport.formattedAccountList.accounts}`,
        }
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data () {
        return {
            ActivityLogSubjectType,
            QueueItemStatus,
            AccountBalanceReport,
            accountBalanceReport: null,
        }
    },
    computed: {
        accountBalanceReportAccounts () {
            if (this.accountBalanceReport.formattedAccountList.teaser) {
                return this.accountBalanceReport.formattedAccountList.teaser.short
            }
            return this.accountBalanceReport.formattedAccountList.accounts
        },
    },
    created () {
        this.fetchObject()
    },
    methods: {
        fetchObject () {
            AccountBalanceReport.objects.get(this.id).then(accountBalanceReport => {
                this.accountBalanceReport = accountBalanceReport
            })
        },
        handleActions (action) {
            switch (action.key) {
                case AccountBalanceReport.action.CANCEL_CREATION:
                    this.$refs.cancelCreationDialog.open()
                    break
                case AccountBalanceReport.action.DELETE:
                    this.$refs.deleteDialog.open()
                    break
            }
        },
        handleCancelCreation () {
            AccountBalanceReport.cancelCreation([this.id])
                .then(accountBalanceReports => {
                    Object.assign(this.accountBalanceReport, accountBalanceReports.pop())
                    this.$refs.cancelCreationDialog.close()

                    this.$q.notify({
                        type: 'positive',
                        message: this.$tc('common.notifications.accounting.account-balance-report-creation-cancelled-success', 1),
                    })
                }).catch(error => {
                    this.$refs.cancelCreationDialog.showFormErrorMessage(error)
                    this.$refs.cancelCreationDialog.resetFormSubmitStatus()
                })
        },
        handleDelete () {
            AccountBalanceReport.delete([this.id])
                .then(() => {
                    this.$refs.deleteDialog.close()
                    this.$router.replace({ name: 'accounting-account-balance-reports' })

                    this.$q.notify({
                        type: 'positive',
                        message: this.$tc('common.notifications.accounting.account-balance-report-deleted-success', 1),
                    })
                })
                .catch(error => {
                    this.$refs.deleteDialog.showFormErrorMessage(error)
                    this.$refs.deleteDialog.resetFormSubmitStatus()
                })
        },
        stopPollingConditionFn (accountBalanceReport) {
            Object.assign(this.accountBalanceReport, accountBalanceReport)
            return !this.accountBalanceReport.currentQueueItem.isQueuedOrProcessing
        },
        hasStatusMessage (accountBalanceReport) {
            return !!accountBalanceReport.currentQueueItem?.status_message
        },
        kebabCase(...args) {
            return kebabCase(...args)
        },
    },
}

</script>
